import React, { useEffect } from 'react';
import { Divider } from 'dw-au-react-components';
import { EmotionBreakpoint } from 'dw-au-react-components';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { useLockBodyScroll } from 'dw-au-react-components';

const StyledModalWrapper = styled.div`
	position: fixed;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 1050;
	margin: 0;
	overflow-x: hidden;
	overflow-y: auto;
	outline: 0;
	width: 100%;
	padding: 15px;
`;

const StyledModalBackdrop = styled.div`
	position: fixed;
	width: 100%;
	height: 100%;
	position: fixed;
	top: 0;
	left: 0;
	z-index: 0;
	/** Theme Background */
	background-color: #222222;
	opacity: 0.85;
`;

const StyledModalContainer = styled.div`
	width: 100%;
	margin: auto;
	z-index: 1;
	position: relative;
	border-radius: 3px;

	.modal-inner {
		${EmotionBreakpoint('mobile')} {
			padding: 20px;
		}
	}

	/** Theme */
	max-width: ${(props) => (props.modalSize ? props.modalSize : '600px')};
	background-color: ${(props) =>
		props.backgroundColor ? props.backgroundColor : ''};
	pointer-events: ${(props) =>
		props.backgroundColor === 'none' ? 'none' : ''};
	display: block;
	opacity: 1;

	animation-name: animateModal;
	animation-duration: 0.3s;
	animation-timing-function: ease-out;
	-webkit-animation-fill-mode: forwards;

	@keyframes animateModal {
		0% {
			top: -20px;
		}
		100% {
			top: 0;
		}
	}

	.modal-wrapper {
		padding: 25px 50px;
		${EmotionBreakpoint('mobile')} {
			padding: 25px 15px;
		}
	}
	.modal-header {
		display: flex;
		align-items: center;
		justify-content: start;
		text-align: left;
		margin-bottom: 15px;

		.modal-title {
			color: rgba(0, 0, 0, 0.6);
			font-style: normal;
			margin: 0;
		}
	}
	.modal-body {
		margin-top: ${(props) => (props.modalHeader ? '35px' : '0')};
	}
`;

const Modal = ({
	showModal,
	toggleModal,
	modalHeader,
	children,
	modalSize,
	disableClose = false,
	backgroundColor,
	modalError = false,
	modalClass,
}) => {
	// Call hook to lock body scroll
	useLockBodyScroll(showModal);

	const closeOnEscapeKeyDown = (e) => {
		if ((e.charCode || e.keyCode) === 27) {
			toggleModal();
		}
	};

	useEffect(() => {
		document.body.addEventListener('keydown', closeOnEscapeKeyDown);
		return function cleanup() {
			document.body.removeEventListener('keydown', closeOnEscapeKeyDown);
		};
	}, []);

	const renderModal = () => {
		return createPortal(
			<StyledModalWrapper>
				<StyledModalContainer
					className={`modal fade ${showModal ? 'showModal' : ''} ${modalClass}`}
					aria-modal
					modalSize={modalSize}
					aria-hidden={`${showModal}`}
					tabIndex={-1}
					backgroundColor={backgroundColor}
				>
					<div className='modal-inner p-50'>
						{modalHeader ? (
							<div className='mb-16'>
								<h6 className='mb-16 lower'>{modalHeader}</h6>

								<Divider />
							</div>
						) : null}
						{modalError && (
							<div className='alert alert-danger mb-16'>{modalError}</div>
						)}
						<div className='modal-body p-0'>{children}</div>
					</div>
					{!disableClose ? (
						<div
							onClick={() => {
								toggleModal();
							}}
							aria-hidden='true'
							className='close'
						></div>
					) : null}
				</StyledModalContainer>
				<StyledModalBackdrop
					className={`modal-backdrop modal-stack fade ${showModal ? 'in' : ''}`}
					onClick={() => {
						toggleModal();
					}}
				/>
			</StyledModalWrapper>,
			document.body
		);
	};

	return <>{showModal ? renderModal() : false}</>;
};

Modal.defaultProps = {
	showModal: false,
	modalHeader: null,
	children: null,
	disableOnClickOutside: false,
	disableClose: false,
	modalSize: '600px',
	backgroundColor: '#ffffff',
	cypressClass: 'modal',
	modalClass: null,
};
Modal.propTypes = {
	modalSize: PropTypes.string,
	showModal: PropTypes.bool,
	modalHeader: PropTypes.node,
	children: PropTypes.node,
	disableOnClickOutside: PropTypes.bool,
	disableClose: PropTypes.bool,
	backgroundColor: PropTypes.string,
	cypressClass: PropTypes.string,
	modalClass: PropTypes.string,
};
export default Modal;
